import React from "react";
import { Facebook, MapPin, Phone, Mail, Bell, ExternalLink } from "lucide-react";
import { clinicInfo, accreditationLogo } from "../mock";
import Logo from "./Logo";

export default function Footer() {
  return (
    <footer className="relative px-4 md:px-8 pb-8">
      <div className="relative mx-auto max-w-[1400px] rounded-[32px] bg-[#1a2464] text-white overflow-hidden">
        <div className="absolute inset-0 wave-pattern opacity-60 pointer-events-none" />

        {/* Newsletter */}
        <div className="relative px-8 md:px-14 py-10 border-b border-white/10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-white/10 grid place-items-center shrink-0">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-xl md:text-2xl font-bold">Important updates waiting for you</div>
              <div className="text-white/60 text-sm mt-1">Health tips, seasonal advice and clinic news.</div>
            </div>
          </div>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="flex items-center bg-white rounded-full p-1 pl-6"
          >
            <input
              type="email"
              placeholder="Your email address"
              className="flex-1 bg-transparent text-[#1a1a2e] placeholder-[#6b6b85]/70 focus:outline-none py-2 text-sm min-w-0"
            />
            <button
              type="submit"
              className="bg-[#1a2464] text-white font-semibold text-sm px-5 md:px-6 py-3 rounded-full hover:bg-[#141c52] shrink-0"
            >
              Subscribe
            </button>
          </form>
        </div>

        {/* Main footer */}
        <div className="relative px-8 md:px-14 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="mb-5">
              <Logo variant="light" size="md" />
            </div>
            <p className="text-white/60 text-sm leading-relaxed max-w-xs">
              The oldest and most established medical practice in Beaudesert, proudly serving the community for over 70 years.
            </p>
            <div className="flex items-center gap-3 mt-5">
              <a href={clinicInfo.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#3dc9b3] grid place-items-center transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div>
            <div className="font-bold text-white mb-5">Quick Links</div>
            <ul className="space-y-3 text-white/70 text-sm">
              <li><a href="/#home" className="hover:text-[#3dc9b3]">Home</a></li>
              <li><a href="/#about" className="hover:text-[#3dc9b3]">About</a></li>
              <li><a href="/#services" className="hover:text-[#3dc9b3]">Services</a></li>
              <li><a href="/#doctors" className="hover:text-[#3dc9b3]">Our Doctors</a></li>
              <li><a href="/#contact" className="hover:text-[#3dc9b3]">Contact</a></li>
            </ul>
          </div>

          <div>
            <div className="font-bold text-white mb-5">Book & Info</div>
            <ul className="space-y-3 text-white/70 text-sm">
              <li>
                <a href={clinicInfo.hotdocUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-[#3dc9b3]">
                  Book on HotDoc <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li><a href="#" className="hover:text-[#3dc9b3]">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-[#3dc9b3]">Terms & Conditions</a></li>
              <li><a href="#" className="hover:text-[#3dc9b3]">Fees & Billing</a></li>
              <li><a href="#" className="hover:text-[#3dc9b3]">Feedback</a></li>
            </ul>
            {accreditationLogo && (
              <div className="mt-5">
                <img src={accreditationLogo} alt="Accreditation badge" className="h-14 w-auto opacity-90" />
              </div>
            )}
          </div>

          <div>
            <div className="font-bold text-white mb-5">Contact Us</div>
            <ul className="space-y-4 text-white/70 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[#3dc9b3] shrink-0" />
                <span>35A William Street<br />Beaudesert QLD 4285</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#3dc9b3] shrink-0" />
                <a href={`tel:${clinicInfo.phoneRaw}`} className="hover:text-white">{clinicInfo.phone}</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#3dc9b3] shrink-0" />
                <a href={`mailto:${clinicInfo.email}`} className="hover:text-white break-all">{clinicInfo.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="relative px-8 md:px-14 py-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-white/60 text-xs">
          <div>Copyright © {new Date().getFullYear()} Beaudesert General Practice. All rights reserved.</div>
          <div>Made with care for the Beaudesert community.</div>
        </div>
      </div>
    </footer>
  );
}
